import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/PageShell";
import { LoadingState } from "../components/Loading";
import { isCoach } from "../lib/auth";
import { useAccountStatus } from "../lib/queries";
import {
  EXAMPLE_SCENES,
  ExampleNote,
  ExampleScene,
  TRACK_GREETING,
  TRACK_LABELS,
  TutorialTrack,
  VISIBLE_TRACKS,
  markTutorialSeen,
  notesForTrack,
} from "../lib/tutorial";

/**
 * How this app works, shown rather than described (finding #28, reworked).
 *
 * This was a stepper: six paragraphs, one at a time, behind a row of chips asking "I'm the coach / I'm
 * a parent / I'm the skater". Both halves of that were wrong. The app already knows who is reading --
 * asking is asking somebody to tell you something you were told at sign-in -- and a wall of prose
 * describes screens the reader then has to go and find, which is the point most people stop.
 *
 * So: an annotated picture of the reader's own screens, with numbered markers they open. The mock is
 * built from the app's real classes, so what they see here is what they will recognise there.
 *
 * Nobody picks a role. What another role's screens are *offered* is decided by VISIBLE_TRACKS -- a
 * coach may read all three, a guardian their own and their skater's, a skater only their own.
 */
export function Tutorial() {
  /**
   * Which screens the reader has chosen to look at, if they have chosen at all. Null means "their own",
   * so re-resolving the role can never throw somebody back to their own screens mid-read.
   */
  const [chosenTrack, setChosenTrack] = useState<TutorialTrack | null>(null);
  const [coach, setCoach] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void isCoach().then((result) => {
      if (!cancelled) setCoach(result);
    });
    markTutorialSeen();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * `not_applicable` means a login with no account of its own, which is exactly a minor's own login
   * (architecture §2c) -- the same fact the account gate reads. Between that and the Cognito coach
   * group, every role is known without anybody being asked.
   */
  const accountStatus = useAccountStatus();

  /**
   * Derived once BOTH answers are in, rather than from whichever arrives first.
   *
   * These resolve independently -- the coach check is local, the account status is a request -- so
   * deciding on the coach answer alone would classify a skater as a guardian and then never revisit it.
   *
   * A failed status read falls to "guardian": it is the common case, the coach group has already been
   * checked by then, and the worst outcome is a parent being shown their own screens, which is what
   * they came for.
   */
  const roleKnown = coach !== null && !accountStatus.isPending;
  const role: TutorialTrack | null = !roleKnown
    ? null
    : coach
      ? "coach"
      : accountStatus.data?.access === "not_applicable"
        ? "athlete"
        : "guardian";
  const track = chosenTrack ?? role;
  const setTrack = setChosenTrack;

  // Waits rather than guessing. Defaulting to "guardian" and correcting a moment later would show a
  // coach the wrong screens first, which is worse than a brief spinner on a page nobody is mid-task on.
  if (!track || !role) return <LoadingState label="Loading…" />;

  const scenes = EXAMPLE_SCENES[track];
  const notes = notesForTrack(track);
  /** Marker numbers run across the whole track, so "③" means one thing on the page, not one per scene. */
  const numberOf = (id: string) => notes.findIndex((note) => note.id === id) + 1;
  const otherTracks = VISIBLE_TRACKS[role].filter((option) => option !== track);

  return (
    <PageShell title="How this works">
      <p className="form-hint">{TRACK_GREETING[track]}</p>

      {scenes.map((scene) => (
        <ExampleSceneView key={scene.id} scene={scene} numberOf={numberOf} />
      ))}

      {/* Not a "who are you" chooser -- the reader was never asked that. This is the quieter question
          of whether they want to see somebody else's screens, offered only to the roles that have a
          reason to (see VISIBLE_TRACKS). */}
      {otherTracks.length > 0 && (
        <section className="tutorial-other-tracks">
          <p className="form-label">Looking for something else?</p>
          <div className="filter-chips" role="group" aria-label="Other guides">
            {otherTracks.map((option) => (
              <button key={option} type="button" className="chip" onClick={() => setTrack(option)}>
                {TRACK_LABELS[option]}
              </button>
            ))}
          </div>
        </section>
      )}
    </PageShell>
  );
}

function ExampleSceneView({ scene, numberOf }: { scene: ExampleScene; numberOf: (id: string) => number }) {
  /**
   * Which note is open. One at a time: several expanded at once turns the annotated screen back into
   * the wall of prose this replaced.
   */
  const [openNote, setOpenNote] = useState<string | null>(null);

  function marker(id: string): ReactNode {
    const note = scene.notes.find((candidate) => candidate.id === id);
    if (!note) return null;
    return (
      <button
        type="button"
        className={openNote === id ? "example-marker open" : "example-marker"}
        aria-expanded={openNote === id}
        aria-label={`${numberOf(id)}. ${note.summary}`}
        onClick={() => setOpenNote((current) => (current === id ? null : id))}
      >
        {numberOf(id)}
      </button>
    );
  }

  return (
    <section className="example-scene">
      <h2>{scene.title}</h2>
      <p className="form-hint">{scene.intro}</p>

      {/* `aria-hidden`: this is a picture of a screen, not a screen. Its fake buttons and headings
          would otherwise be announced as things to use, and a screen-reader user would be navigating a
          decoy. The markers sit outside it and carry the whole meaning, which is why they are labelled
          with their own summary text rather than just a number. */}
      <div className="example-screen" aria-hidden="true">
        <SceneMock sceneId={scene.id} />
      </div>

      <ol className="example-notes">
        {scene.notes.map((note) => (
          <li key={note.id} className={openNote === note.id ? "example-note open" : "example-note"}>
            <div className="example-note-head">
              {marker(note.id)}
              <button
                type="button"
                className="example-note-summary"
                aria-expanded={openNote === note.id}
                onClick={() => setOpenNote((current) => (current === note.id ? null : note.id))}
              >
                {note.summary}
              </button>
            </div>
            {openNote === note.id && <NoteBody note={note} />}
          </li>
        ))}
      </ol>
    </section>
  );
}

function NoteBody({ note }: { note: ExampleNote }) {
  return (
    <div className="example-note-body">
      <p>{note.body}</p>
      {/* Every note that has a real place to go links there, so reading this ends with somebody in the
          app rather than at the bottom of a help page. */}
      {note.link && (
        <Link className="link-button" to={note.link.to}>
          {note.link.label}
        </Link>
      )}
    </div>
  );
}

/**
 * The mocked screens.
 *
 * Deliberately built from the app's own class names rather than bespoke help-page styling: the point is
 * that the reader recognises the real thing when they get to it, and a picture drawn in a different
 * visual language would not help them do that. Kept in this file rather than in `tutorial.ts` because
 * it is layout -- the copy the markers carry is what lives in the data.
 *
 * The numbers are rendered by the caller and positioned by CSS, so a scene's markup does not have to
 * know which number it is showing.
 */
function SceneMock({ sceneId }: { sceneId: string }): ReactNode {
  switch (sceneId) {
    case "coach-roster":
      return (
        <>
          <div className="open-flags">
            <h2>Needs your attention</h2>
            <ul className="open-flags-list">
              <li className="open-flag stopped">
                <div className="open-flag-body">
                  <p className="open-flag-who">
                    Chloe Whitfield <span className="open-flag-when">2 hours ago</span>
                  </p>
                  <p className="open-flag-what">
                    Their guardian reported a problem on Squat jumps, set 1 and stopped the session there.
                  </p>
                  <p className="open-flag-note">&ldquo;Right knee hurt on the landings&rdquo;</p>
                </div>
                <span className="chip">Mark as seen</span>
              </li>
            </ul>
            <ul className="open-flags-list quiet-list">
              <li className="open-flag">
                <div className="open-flag-body">
                  <p className="open-flag-who">Devon Reyes</p>
                  <p className="open-flag-what">No session logged for 14 days.</p>
                </div>
                <span className="chip">Message</span>
              </li>
            </ul>
          </div>

          <div className="toolbar">
            <span className="mock-input">Search clients by name</span>
            <div className="filter-chips">
              <span className="chip active">All</span>
              <span className="chip">No plan</span>
            </div>
          </div>

          <ul className="client-list">
            <li className="client-card plan-card">
              <div className="plan-card-main">
                <span className="client-avatar">MN</span>
                <span className="client-name">Maya Novak</span>
                <span className="badge">Intermediate · age 9</span>
                <span className="badge badge-warn">Leaving on September 7</span>
                <span className="link-button">Open</span>
              </div>
            </li>
            <li className="client-card plan-card">
              <div className="plan-card-main">
                <span className="client-avatar">EN</span>
                <span className="client-name">Ellis Novak</span>
                <span className="badge">Juvenile · age 12</span>
                <span className="link-button">Open</span>
              </div>
            </li>
          </ul>
          <span className="mock-button">Add an athlete</span>
        </>
      );

    case "coach-builder":
      return (
        <>
          <p className="stage-progress">Plan builder — Maya Novak</p>
          <span className="mock-input">Tuesday off-ice</span>
          <div className="example-row">
            <span className="element-tag">E1</span>
            <span className="element-name">Squat jumps</span>
            <span className="badge badge-ok">Video</span>
          </div>
          <div className="exercise-row labelled">
            <span className="field">
              <span className="field-label">Sets</span>
              <span className="mock-input">3</span>
            </span>
            <span className="field">
              <span className="field-label">Reps</span>
              <span className="mock-input">8</span>
            </span>
            <span className="field">
              <span className="field-label">Load</span>
              <span className="mock-input">bodyweight</span>
            </span>
          </div>
          <div className="example-row circuit">
            <span className="circuit-label">Core circuit — 3 rounds · rest 60s between rounds</span>
            <span className="element-name">Single-leg balance · Plank</span>
          </div>
          <div className="filter-chips">
            <span className="chip">Save as new draft</span>
            <span className="mock-button">Publish</span>
          </div>
          <div className="example-row">
            <span className="client-name">Sat, Aug 8</span>
            <span className="badge badge-ok">Completed</span>
            <span className="badge">RPE 7</span>
            <span className="chip">Full details</span>
          </div>
        </>
      );

    case "guardian-home":
      return (
        <>
          <p className="family-billing-line">
            <span>$210.00 a month</span>
            <span className="link-button">Billing</span>
          </p>
          <ul className="client-list">
            <li className="client-card plan-card">
              <div className="plan-card-main">
                <span className="client-avatar">MN</span>
                <span className="client-name">Maya Novak</span>
                <span className="link-button">View plan</span>
                <span className="link-button">Messages</span>
              </div>
              <p className="athlete-status">Trained today · 3 this week</p>
              <div className="skater-login">
                <span className="chip">Create a login for Maya</span>
              </div>
            </li>
            <li className="client-card plan-card">
              <div className="plan-card-main">
                <span className="client-avatar">EN</span>
                <span className="client-name">Ellis Novak</span>
                <span className="badge">Ellis logs their own</span>
                <span className="link-button">View plan</span>
                <span className="link-button">Messages</span>
              </div>
              <p className="athlete-status flagged">
                <span className="athlete-status-flag">Reported a problem · </span>Trained yesterday
              </p>
            </li>
          </ul>
        </>
      );

    case "guardian-settings":
      return (
        <>
          <h2>Billing</h2>
          <p>
            <span className="badge badge-ok">Your subscription is active</span>
          </p>
          <p>
            Your next payment is <strong>$210.00</strong> on <strong>7 September 2026</strong>.
          </p>
          <ul className="goal-list">
            <li>Maya Novak: Full programme — $120.00/mo</li>
            <li>Ellis Novak: Full programme — $90.00/mo</li>
            <li>
              <strong>$210.00 a month</strong> — includes 25% off for each additional athlete
            </li>
          </ul>
          <div className="filter-chips">
            <span className="chip">Change Maya&rsquo;s plan</span>
            <span className="chip">Stop Maya&rsquo;s training only</span>
          </div>
          <h2>Consent</h2>
          <ul className="goal-list">
            <li>You agreed to the Privacy Policy on 7 August 2026</li>
            <li>You gave parental consent for your athlete on 7 August 2026</li>
          </ul>
          <span className="mock-quiet">Cancel everything and end my subscription</span>
        </>
      );

    case "athlete-session":
      return (
        <>
          <div className="example-row">
            <span className="session-estimate">About 14 minutes · 7 blocks</span>
            <span className="mock-button">Start workout</span>
          </div>
          <div className="walkthrough-card">
            <p className="stage-progress">
              Exercise 1/5
              <span className="stage-progress-set">Set 2/3</span>
            </p>
            <p className="exercise-name">Squat jumps</p>
            <p className="element-prescription">3 × 8</p>
            <span className="field-label">How many reps did you actually do?</span>
            <span className="mock-input">8</span>
            <span className="report-issue">Something hurt or went wrong</span>
            <span className="mock-button">Complete</span>
          </div>
          <div className="example-rest">
            <p className="stage-label">Rest</p>
            <p className="stage-number">45s</p>
            <p className="stage-sub">Next up: Single-leg balance</p>
          </div>
          <div className="example-row">
            <span className="stage-sub">How hard was that?</span>
            <div className="filter-chips">
              {[5, 6, 7, 8].map((value) => (
                <span key={value} className={value === 7 ? "chip active" : "chip"}>
                  {value}
                </span>
              ))}
            </div>
          </div>
        </>
      );

    default:
      return null;
  }
}
